/* Optional Mystery Tech. Shopify owns cart prices; the supplier owns allocation. */
(() => {
  'use strict';
  if (window.EvolvedMystery) return;
  const node = document.getElementById('evolved-mystery-config');
  if (!node) return;
  let config;
  try { config = JSON.parse(node.textContent); } catch (_) { return; }
  if (!Number.isSafeInteger(config.variantId) || !(config.price > 0)) return;
  const s = config.strings;
  const mounts = new Set();
  const isMystery = item => String(item.product_id) === String(config.productId);
  const isProtection = item => ['evolved-shipping-protection', 'navidium-shipping-protection'].includes(item.handle) || /Shipping Protection/i.test(item.product_type || item.product_title || '');
  const merchandise = current => current.items.filter(item => !isMystery(item) && !isProtection(item) && item.requires_shipping && !item.gift_card && item.final_line_price > 0);
  const mysteryLines = current => current.items.filter(isMystery);
  // Fulfillment matches the final order; no device selection is required.
  const validLine = (line, current) => merchandise(current).length > 0 && line.properties?._ev_mystery === '1';
  const signature = current => JSON.stringify(current.items.filter(item => !isProtection(item)).map(item => [item.key, item.quantity, item.variant_id, item.properties]));
  const rawRequest = async (path, body) => {
    const controller = new AbortController(), timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch((window.Shopify?.routes?.root || '/') + path, { method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', signal: controller.signal, headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, ...(body ? { body: JSON.stringify(body) } : {}) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'request');
      return data;
    } finally { clearTimeout(timeout); }
  };
  const request = async (path, body) => {
    const data = await rawRequest(path, body);
    return path.startsWith('cart/') && path !== 'cart/add.js' && body?.discount === undefined && Array.isArray(data.items) && window.EvolvedCartDiscounts
      ? window.EvolvedCartDiscounts.restore(data, rawRequest) : data;
  };
  const sectionIds = () => {
    const ids = [], page = document.querySelector('.ev-cart-page__summary')?.closest('.shopify-section');
    if (page) ids.push(page.id.replace('shopify-section-', ''));
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: ids } }));
    return [...new Set(ids)].slice(0, 5);
  };
  let cart = config.cart, pending = 0, work = Promise.resolve(), notice = '', timer, checkoutPass = '', checkingOut = false, activeAdd = false;
  const nativeBusy = () => document.querySelector('line-item-quantity[aria-busy="true"], product-form [aria-busy="true"], [data-ev-add][aria-busy="true"]');
  const waitForNative = async () => {
    const deadline = Date.now() + 12000;
    while (nativeBusy()) {
      if (Date.now() > deadline) throw new Error('busy');
      await new Promise(resolve => setTimeout(resolve, 80));
    }
  };
  const paint = () => mounts.forEach(mount => mount.paint());
  const queue = fn => {
    pending++; paint();
    const next = work.then(async () => {
      await window.EvolvedShippingProtection?.pause('mystery');
      try { await waitForNative(); return await fn(); }
      finally { window.EvolvedShippingProtection?.resume('mystery'); }
    }).finally(() => { pending--; paint(); });
    work = next.catch(() => {});
    return next;
  };
  const refresh = async current => {
    const ids = sectionIds();
    if (ids.length && !ids.every(id => typeof current.sections?.[id] === 'string')) current.sections = await request('?' + new URLSearchParams({ sections: ids.join(',') }));
    cart = current;
    if (ids.length && ids.every(id => typeof current.sections?.[id] === 'string')) {
      const page = document.querySelector('.ev-cart-page__summary')?.closest('.shopify-section');
      if (page) {
        const html = current.sections[page.id.replace('shopify-section-', '')];
        const replacement = new DOMParser().parseFromString(html, 'text/html').getElementById(page.id);
        if (replacement) page.replaceChildren(...replacement.childNodes);
      }
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'evolved:mystery', cart: current } }));
    } else if (ids.length) {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }
    paint();
  };
  const normalise = async (current, render = true) => {
    const updates = {}; let kept = false, removed = false;
    for (const item of mysteryLines(current)) {
      if (!kept && validLine(item, current)) { kept = true; if (item.quantity !== 1) updates[item.key] = 1; }
      else { updates[item.key] = 0; removed = true; }
    }
    let changed = false;
    if (Object.keys(updates).length) {
      current = await request('cart/update.js', { updates, sections: sectionIds(), sections_url: location.pathname });
      changed = true;
      if (removed) notice = s.cartChanged;
    }
    // Migrate existing cart lines without losing the product or unrelated properties.
    const retained = mysteryLines(current)[0];
    if (retained && (retained.properties?.['Match to'] || retained.properties?._ev_mystery_match !== 'order')) {
      const properties = { ...retained.properties, _ev_mystery_match: 'order', _ev_mystery_rules: config.rules };
      for (const key of ['Match to', '_ev_mystery_profile', '_ev_mystery_choice', '_ev_mystery_source_variant', '_ev_mystery_source_product']) delete properties[key];
      current = await request('cart/change.js', { id: retained.key, quantity: 1, properties, sections: sectionIds(), sections_url: location.pathname });
      changed = true;
    }
    if (changed && render) await refresh(current);
    cart = current; return current;
  };
  const update = async (mount, adding = true) => {
    if (pending || nativeBusy()) { notice = s.wait; paint(); return; }
    notice = '';
    const oldPrice = config.price;
    activeAdd = adding;
    await queue(async () => {
      let attemptedToken = '';
      try {
        let current = await request('cart.js');
        if (!adding) {
          const updates = Object.fromEntries(mysteryLines(current).map(item => [item.key, 0]));
          if (Object.keys(updates).length) current = await request('cart/update.js', { updates, sections: sectionIds(), sections_url: location.pathname });
          await refresh(current); return;
        }
        current = await normalise(current, false);
        if (mysteryLines(current).length) { mount.close(); await refresh(current); return; }
        if (!merchandise(current).length) throw new Error('changed');
        if (current.currency !== config.currency || !config.available) throw new Error('price');
        attemptedToken = crypto.randomUUID();
        const properties = { _ev_mystery: '1', _ev_mystery_match: 'order', _ev_mystery_rules: config.rules, _ev_mystery_minimum_usd: '17.95', _ev_mystery_reference_minimum_usd: '24.95', _ev_mystery_token: attemptedToken };
        await request('cart/add.js', { items: [{ id: config.variantId, quantity: 1, properties }], sections: sectionIds(), sections_url: location.pathname });
        current = await request('cart.js');
        const added = current.items.find(item => item.properties?._ev_mystery_token === attemptedToken);
        if (!added || current.currency !== config.currency || Math.abs(added.original_price - oldPrice) > 1) throw new Error('price');
        if (!validLine(added, current)) throw new Error('changed');
        mount.close();
        await refresh(current);
        document.documentElement.dispatchEvent(new CustomEvent('evolved:mystery-added', { bubbles: true, detail: { variantId: config.variantId, matching: 'order' } }));
      } catch (error) {
        notice = error.message === 'changed' ? s.cartChanged : error.message === 'price' ? s.priceChanged : s.error;
        // A timed-out add can have succeeded. Roll back only our attempted line.
        try {
          let latest = await request('cart.js');
          const own = latest.items.filter(item => attemptedToken && item.properties?._ev_mystery_token === attemptedToken);
          if (own.length) latest = await request('cart/update.js', { updates: Object.fromEntries(own.map(item => [item.key, 0])), sections: sectionIds(), sections_url: location.pathname });
          await refresh(latest);
        } catch (_) { notice = s.network; }
      }
    }).catch(() => { notice = s.error; paint(); }).finally(() => { activeAdd = false; paint(); });
  };
  class EvolvedMystery extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      try {
        const quote = JSON.parse(this.querySelector('[data-mystery-quote]').textContent);
        if (quote.currency === config.currency && quote.price > 0) Object.assign(config, quote);
        if (!pending) cart = JSON.parse(this.querySelector('[data-mystery-cart]').textContent);
      } catch (_) {}
      try { this.values = JSON.parse(this.querySelector('[data-mystery-values]')?.textContent || 'null'); } catch (_) { this.values = null; }
      this.referenceNodes = [...this.querySelectorAll('[data-mystery-reference]')].map(element => ({ element, key:element.dataset.mysteryReference, template:element.textContent }));
      this.dialog = this.querySelector('dialog');
      this.querySelector('[data-mystery-details]').addEventListener('click', event => { event.preventDefault(); event.stopPropagation(); this.open(); }, { signal });
      this.querySelector('[data-mystery-close]').addEventListener('click', () => this.close(), { signal });
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); }, { signal });
      this.dialog.addEventListener('click', event => { if (event.target === this.dialog) { const r = this.dialog.getBoundingClientRect(); if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.close(); } }, { signal });
      this.querySelector('[data-mystery-card]').addEventListener('click', () => update(this), { signal });
      this.querySelector('[data-mystery-add]').addEventListener('click', () => update(this), { signal });
      // Impact can replace the cart lines after the footer has connected.
      // Refresh the visible value only when a new Mystery Tech line is inserted.
      const scope = this.closest('#cart-drawer, .shopify-section--main-cart');
      if (scope) {
        this.lineObserver = new MutationObserver(records => {
          const hasNewValue = records.some(record => [...record.addedNodes].some(element =>
            element.nodeType === 1 && (element.matches('[data-mystery-line-value]') || element.querySelector('[data-mystery-line-value]'))
          ));
          if (hasNewValue) this.renderReferences();
        });
        this.lineObserver.observe(scope, { childList: true, subtree: true });
      }
      mounts.add(this); this.paint();
    }
    disconnectedCallback() { this.close(); this.lineObserver?.disconnect(); this.lineObserver = null; this.controller?.abort(); this.controller = null; mounts.delete(this); }
    open() { this.returnFocus = document.activeElement; this.paint(); if (!this.dialog.open) this.dialog.showModal(); }
    close() { if (this.dialog?.open) this.dialog.close(); if (this.returnFocus?.isConnected && this.returnFocus.matches(':focus-visible')) this.returnFocus.focus({ preventScroll: true }); }
    renderReferences() {
      const values = this.values, valid = values?.valid && values.currency === cart.currency;
      const substitute = template => template.replaceAll('[minimum]', values?.minimum || '').replaceAll('[maximum]', values?.maximum || '').replaceAll('[currency]', cart.currency);
      for (const {element,key,template} of this.referenceNodes || []) {
        if (['essentials','plus','premium','ultimate'].includes(key)) {
          const range = values?.[key];
          element.textContent = valid && Array.isArray(range) ? [...new Set(range)].join('–') : s.valueUnavailable;
        } else if (key === 'value_note') element.textContent = substitute(template);
        else if (valid) element.textContent = substitute(template);
        else element.textContent = key === 'teaser' ? s.teaserUnavailable : key === 'guarantee' ? s.guaranteeUnavailable : key === 'rules_value' ? s.rulesValueUnavailable : s.valueUnavailable;
      }
      const scope = this.closest('#cart-drawer, .shopify-section--main-cart') || this.parentElement;
      scope?.querySelectorAll('[data-mystery-line-value]').forEach(element => { element.textContent = valid ? substitute(element.dataset.mysteryTemplate) : s.teaserUnavailable; });
    }
    paint() {
      this.renderReferences();
      const checked = mysteryLines(cart).length > 0, eligible = merchandise(cart).length > 0;
      this.hidden = !checked && (!eligible || !config.available);
      this.dataset.added = String(checked);
      this.setAttribute('aria-busy', pending ? 'true' : 'false');
      this.querySelector('[data-mystery-offer]').hidden = checked;
      const card = this.querySelector('[data-mystery-card]');
      card.disabled = pending > 0 || checked || !eligible || !config.available;
      card.setAttribute('aria-label', s.add + ' — ' + config.formatted + '. ' + (this.querySelector('[data-mystery-reference="teaser"]')?.textContent || s.teaserUnavailable || ''));
      this.querySelector('[data-mystery-price]').textContent = config.formatted;
      this.querySelector('[data-mystery-card-action]').textContent = pending && activeAdd ? s.updating : (s.addShort || 'Add');
      this.querySelector('[data-mystery-status]').textContent = notice;
      this.querySelector('[data-mystery-dialog-status]').textContent = notice;
      const button = this.querySelector('[data-mystery-add]');
      button.textContent = pending && activeAdd ? s.updating : checked ? s.added : s.add + ' — ' + config.formatted;
      button.disabled = pending > 0 || checked || !eligible || !config.available;
    }
  }
  // Impact's drawer has its own focus trap. Handle the nested modal first so
  // Escape closes only these details and Tab stays inside the visible dialog.
  document.addEventListener('keydown', event => {
    const mount = [...mounts].find(value => value.dialog?.open);
    if (!mount) return;
    if (event.key === 'Escape') { event.preventDefault(); event.stopImmediatePropagation(); mount.close(); }
    else if (event.key === 'Tab') {
      event.stopImmediatePropagation();
      const focusable = [...mount.dialog.querySelectorAll('button:not(:disabled), select:not(:disabled), a[href], summary')].filter(element => element.getClientRects().length && !element.hidden && (!element.closest('details:not([open])') || element.tagName === 'SUMMARY'));
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }
  }, true);
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-mystery-line-details]');
    if (!trigger) return;
    const scope = trigger.closest('#cart-drawer, .shopify-section--main-cart');
    const mount = scope?.querySelector('evolved-mystery');
    if (mount) { event.preventDefault(); mount.open(); }
  });
  customElements.define('evolved-mystery', EvolvedMystery);
  const schedule = () => {
    clearTimeout(timer);
    if (!mysteryLines(cart).length) return;
    timer = setTimeout(() => {
      if (pending) { schedule(); return; }
      queue(async () => normalise(await request('cart.js'))).catch(() => { notice = s.error; paint(); });
    }, 300);
  };
  document.addEventListener('cart:change', event => {
    if (!event.detail?.cart?.items) return;
    cart = event.detail.cart; paint();
    if (event.detail.baseEvent !== 'evolved:mystery' && event.detail.baseEvent !== 'evolved:protection') schedule();
  });
  document.addEventListener('cart:refresh', () => {
    work.then(async () => { cart = await request('cart.js'); paint(); schedule(); }).catch(() => {});
  });
  window.addEventListener('pageshow', event => { if (event.persisted) request('cart.js').then(current => { cart = current; paint(); schedule(); }).catch(() => {}); });
  // Only gate a cart containing this add-on (or an add still in progress).
  // The verified merchandise signature survives protection's own submit pass.
  document.addEventListener('submit', event => {
    if (!(event.target instanceof HTMLFormElement) || event.submitter?.name !== 'checkout') return;
    if (!pending && (!mysteryLines(cart).length || checkoutPass === signature(cart))) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (checkingOut) return;
    checkingOut = true;
    const form = event.target, name = event.submitter.name;
    queue(async () => {
      const current = await normalise(await request('cart.js'), false);
      checkoutPass = signature(current);
    }).then(() => {
      const nextForm = form.isConnected ? form : document.querySelector('.ev-cart-page__summary form, form[action$="/cart"]');
      const button = nextForm?.querySelector('[name="' + name + '"]');
      if (nextForm && button) nextForm.requestSubmit(button);
    }).catch(() => { notice = s.error; paint(); }).finally(() => { checkingOut = false; });
  }, true);
  window.EvolvedMystery = { isBusy: () => pending > 0 };
  schedule();
})();

